import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextLink from "next/link";
import type React from "react";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";

const TERMS_OF_SERVICE_QUERY = b.query("TermsOfService", (b) => [b.__fragment(HEADER_FRAGMENT)]);

const LAST_UPDATED = "July 9, 2026";

const linkClass =
  "font-semibold text-brand-black underline underline-offset-2 hover:text-brand-mint transition-colors";

const P: React.FC<React.PropsWithChildren> = ({ children }) => (
  <p className="mt-4 text-sm lg:text-base text-gray-600 leading-relaxed">{children}</p>
);

const TermsOfService: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props,
) => {
  const { data } = useHydratedQuery(props);

  return (
    <>
      <Head>
        <title>{`Terms of Service | Visionary Meals`}</title>
        <meta
          name="description"
          content="Mobile Terms of Service for Visionary Meals' SMS/text messaging program."
        />
      </Head>
      <PageLayout data={data}>
        <div className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
          <h1 className="text-3xl lg:text-4xl font-bold text-brand-black">
            Mobile Terms of Service
          </h1>
          <p className="mt-2 text-xs text-gray-400">Visionary Meals</p>
          <p className="mt-1 text-xs text-gray-400">Last updated: {LAST_UPDATED}</p>

          <P>
            The Visionary Meals mobile message service (the &ldquo;Service&rdquo;) is operated by
            Visionary Meals (&ldquo;Visionary Meals&rdquo;, &ldquo;we&rdquo;, or &ldquo;us&rdquo;).
            Your use of the Service constitutes your agreement to these terms and conditions
            (&ldquo;Mobile Terms&rdquo;). We may modify or cancel the Service or any of its
            features without notice. To the extent permitted by applicable law, we may also modify
            these Mobile Terms at any time and your continued use of the Service following the
            effective date of any such changes shall constitute your acceptance of such changes.
          </P>

          <P>
            By consenting to Visionary Meals&rsquo;s SMS/text messaging service, you agree to
            receive recurring SMS/text messages from and on behalf of Visionary Meals through your
            wireless provider to the mobile number you provided, even if your mobile number is
            registered on any state or federal Do Not Call list. Text messages may be sent using an
            automatic telephone dialing system or other technology. Service-related messages may
            include updates, alerts, and information (e.g., order updates, account alerts, etc.).
            Promotional messages may include promotions, specials, and other marketing offers
            (e.g., cart reminders).
          </P>

          <P>
            You understand that you do not have to sign up for this program in order to make any
            purchases, and your consent is not a condition of any purchase with Visionary Meals.
            Your participation in this program is completely voluntary.
          </P>

          <P>
            We do not charge for the Service, but you are responsible for all charges and fees
            associated with text messaging imposed by your wireless provider. Message frequency
            varies. Message and data rates may apply. Check your mobile plan and contact your
            wireless provider for details. You are solely responsible for all charges related to
            SMS/text messages, including charges from your wireless provider.
          </P>

          <P>
            You may opt-out of the Service at any time. Text the single keyword command STOP to{" "}
            <NextLink href="tel:+18444810200" className={linkClass}>
              +1 (844) 481-0200
            </NextLink>{" "}
            or click the unsubscribe link (where available) in any text message to cancel.
            You&apos;ll receive a one-time opt-out confirmation text message. No further messages
            will be sent to your mobile device, unless initiated by you. If you have subscribed to
            other Visionary Meals mobile message programs and wish to cancel, except where
            applicable law requires otherwise, you will need to opt out separately from those
            programs by following the instructions provided in their respective mobile terms.
          </P>

          <P>
            For Service support or assistance, text HELP to{" "}
            <NextLink href="tel:+18444810200" className={linkClass}>
              +1 (844) 481-0200
            </NextLink>{" "}
            or email{" "}
            <NextLink href="mailto:info@visionarymeals.com" className={linkClass}>
              info@visionarymeals.com
            </NextLink>
            .
          </P>

          <P>
            We may change any short code or telephone number we use to operate the Service at any
            time and will notify you of these changes. You acknowledge that any messages,
            including any STOP or HELP requests, you send to a short code or telephone number we
            have changed may not be received and we will not be responsible for honoring requests
            made in such messages.
          </P>

          <P>
            The wireless carriers supported by the Service are not liable for delayed or
            undelivered messages. You agree to provide us with a valid mobile number. If you get a
            new mobile number, you will need to sign up for the program with your new number.
          </P>

          <P>
            To the extent permitted by applicable law, you agree that we will not be liable for
            failed, delayed, or misdirected delivery of any information sent through the Service,
            any errors in such information, and/or any action you may or may not take in reliance
            on the information or Service.
          </P>

          <P>
            We respect your right to privacy. To see how we collect and use your personal
            information, please see our{" "}
            <NextLink href="/privacy-policy" className={linkClass}>
              Privacy Policy
            </NextLink>
            .
          </P>
        </div>
      </PageLayout>
    </>
  );
};

export default TermsOfService;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  return { props: await getApolloSSRProps(TERMS_OF_SERVICE_QUERY.document(), {}, ctx) };
});
